import React, { useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ContentSlider } from '../../common/ui';

const styles = cssMap({
	floatingPositioner: {
		position: 'fixed',
		bottom: token('space.100'),
		zIndex: 400,
		pointerEvents: 'none',
		display: 'flex',
		justifyContent: 'center',
		transition: 'width 300ms cubic-bezier(0.2, 0, 0, 1)',
	},
	bottomAlignment: {
		bottom: token('space.300'),
	},
});

const MARGIN_BUFFER = 200; // Constant for margin from container edges

export const usePrevious = <T,>(value: T): T | undefined => {
	const ref = useRef<T | undefined>();
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

export const ToolbarWrapper = ({
	children,
	show,
	minToolbarWidth,
	width,
	onDeselectAll,
}: {
	children: (maxToolbarWidth?: number) => React.ReactNode;
	onDeselectAll?: () => void;
	show: boolean;
	minToolbarWidth?: number;
	width?: string;
}): React.JSX.Element => {
	const previousShow = usePrevious(show);
	const containerRef = useRef<HTMLDivElement>(null);
	const [maxToolbarWidth, setMaxToolbarWidth] = useState(0);

	useEffect(() => {
		if (previousShow && !show) {
			onDeselectAll?.();
		}
	}, [onDeselectAll, previousShow, show]);

	useEffect(() => {
		if (fg('responsive_bulk_ops_toolbar_platform')) {
			const container = containerRef.current;
			if (!container) {
				return;
			}

			const updateMaxWidth = () => {
				const containerWidth = container.offsetWidth;
				const calculatedMaxWidth = Math.max(minToolbarWidth ?? 0, containerWidth - MARGIN_BUFFER);
				setMaxToolbarWidth(calculatedMaxWidth);
			};

			// Create ResizeObserver to watch container size changes
			const resizeObserver = new ResizeObserver(updateMaxWidth);
			resizeObserver.observe(container);

			// Initial calculation
			updateMaxWidth();

			return () => {
				resizeObserver.disconnect();
			};
		}
	}, [minToolbarWidth]);

	return (
		<Box
			ref={fg('responsive_bulk_ops_toolbar_platform') ? containerRef : null}
			xcss={cx(
				styles.floatingPositioner,
				// on clean up merge bottomAlignment into floatingPositioner
				fg('responsive_bulk_ops_toolbar_platform') && styles.bottomAlignment,
			)}
			style={{ width: width || '100%' }}
		>
			<ContentSlider show={show}>
				{children(fg('responsive_bulk_ops_toolbar_platform') ? maxToolbarWidth : undefined)}
			</ContentSlider>
		</Box>
	);
};
